import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { MembershipFacade } from '@features/membership/facades/membership.facade';
import {
  CreateRewardsPreference,
  CreateRewardsPreferenceFail,
  RewardsPreferenceActionTypes as actions,
} from '@features/rewards-preference/store/actions/rewards-preference.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import { LoyaltyProgram } from '@rd/types';
import { of } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class SelectCarrierEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private dialogService: DialogService,
    private membershipFacade: MembershipFacade,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
    private translateService: TranslateService,
  ) {}

  createRewardsPreferenceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.CREATE_REWARDS_PREFERENCE_SUCCESS),
        tap(() => {
          this.router.navigateByUrl('/');
        }),
      ),
    {
      dispatch: false,
    },
  );

  createRewardsPreferenceFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.CREATE_REWARDS_PREFERENCE_FAIL),
        withLatestFrom(
          this.actions$.pipe(ofType(actions.CREATE_REWARDS_PREFERENCE)),
        ),
        switchMap(
          ([_, createAction]: [
            CreateRewardsPreferenceFail,
            CreateRewardsPreference,
          ]) => {
            if ('loyaltyProgramId' in createAction.payload.membership) {
              return of(createAction.payload.membership.loyaltyProgramId);
            }

            const membershipId = createAction.payload.membership.membershipId;
            return this.membershipFacade.pointsTransferMemberships$.pipe(
              map(
                (memberships) =>
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  memberships.find(
                    (membership) => membership.id === membershipId,
                  )!.loyaltyProgramId,
              ),
            );
          },
        ),
        switchMap((loyaltyProgramId) => {
          return this.loyaltyProgramFacade.loyaltyProgramsMap$.pipe(
            map((loyaltyProgramMap) => loyaltyProgramMap[loyaltyProgramId]),
          );
        }),
        switchMap((loyaltyProgram: LoyaltyProgram) => {
          return this.dialogService.open(CheckoutErrorDialogComponent, {
            data: {
              ctaButtonText: this.translateService.instant(
                'select_carrier.rewards_ref.error.ok',
              ),
              errorMessageDescription: this.translateService.instant(
                'select_carrier.rewards_ref.error.description',
                {
                  loyaltyProgram: loyaltyProgram.shortName,
                },
              ),
              errorMessageTitle: this.translateService.instant(
                'select_carrier.rewards_ref.error.title',
              ),
              iconName: 'x-sorry',
            },
            id: 'rd-shared-checkout-error-dialog',
            screenMode: 'center-md',
          }).afterClosed;
        }),
        tap(() => {
          document
            .querySelector<HTMLButtonElement>('.select-carrier-submit button')
            ?.focus();
        }),
      ),
    {
      dispatch: false,
    },
  );
}
